import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Box, Flex, Grid, HStack, VStack, Spinner } from '@chakra-ui/react'
import { AdvancedMarker, InfoWindow } from '@vis.gl/react-google-maps'
import {
  LuArrowLeft,
  LuClock,
  LuMapPin,
  LuRefreshCw,
  LuSiren,
  LuTriangleAlert,
  LuCircle,
} from 'react-icons/lu'
import { Text, useT } from '@minetrace/ui'
import { brand, themeColors } from '../../../theme'
import { plantGateService } from '../../../api/services/plantGate.service'
import type {
  PlantGateAlertResponse,
  PlantGateDetailResponse,
  PlantGateEstado,
  PlantGateEtaState,
} from '../../../api/models/plantGate.models'
import { parseUtcDate } from '../../../lib/dates'
import { KMap, Polyline, FitBounds } from '../../../lib/googleMaps'
import { ElementPin } from '../../../lib/mapMarkers'

type LL = google.maps.LatLngLiteral

// ────────────────────────────────────────────────────────────────────
// Helpers

function fmtMinutes(m: number) {
  if (m < 60) return `${m} min`
  const h = Math.floor(m / 60)
  const r = m % 60
  return r === 0 ? `${h}h` : `${h}h ${r}m`
}

const ETA_COPY: Record<PlantGateEtaState, { color: string; label: (m: number | null) => string }> =
  {
    EnRoute: { color: '#2a8a87', label: (m) => (m == null ? 'En ruta' : fmtMinutes(m)) },
    AtGate: { color: '#d49a2a', label: () => 'En garita' },
    Stopped: {
      color: '#3d6fa8',
      label: (m) => (m == null ? 'Detenido' : `Detenido · ≈ ${fmtMinutes(m)}`),
    },
    Stale: {
      color: '#b5aa9d',
      label: (m) => (m == null ? 'Señal antigua' : `Señal antigua · ≈ ${fmtMinutes(m)}`),
    },
    Unknown: { color: '#857b71', label: () => '—' },
    // El conductor finalizó el viaje: no hay ETA que proyectar.
    Finished: { color: '#b4532a', label: () => 'Llegada declarada' },
  }

const ESTADO_COLORS: Record<PlantGateEstado, string> = {
  Conforme: '#2a8a87',
  Observado: '#EAB308',
  NoConforme: '#c8442f',
  Pendiente: '#b5aa9d',
  Validando: '#b5aa9d',
}

function formatDateTime(iso: string | null) {
  if (!iso) return '—'
  const d = parseUtcDate(iso)
  return (
    d.toLocaleDateString('es-PE', { day: '2-digit', month: 'short' }) +
    ' ' +
    d.toLocaleTimeString('es-PE', { hour: '2-digit', minute: '2-digit' })
  )
}

function formatDistance(meters: number | null) {
  if (meters == null) return '—'
  if (meters < 1000) return `${Math.round(meters)} m`
  return `${(meters / 1000).toFixed(1)} km`
}

// ────────────────────────────────────────────────────────────────────
// Sections

function Field({ label, value }: { label: string; value: React.ReactNode }) {
  const colors = themeColors
  return (
    <VStack gap={0.5} alignItems="flex-start">
      <Text fontSize="11px" color={colors.textMuted} textTransform="uppercase" letterSpacing="0.06em">
        {label}
      </Text>
      <Text fontSize="sm" color={colors.text} fontWeight="500">
        {value || '—'}
      </Text>
    </VStack>
  )
}

function AlertCard({ alert }: { alert: PlantGateAlertResponse }) {
  const colors = themeColors
  const estadoColor = ESTADO_COLORS[alert.estado] ?? '#b5aa9d'
  const isPending = alert.estado === 'Pendiente' || alert.estado === 'Validando'

  // Outcome-based border:
  //   Conforme              → green
  //   NoConforme on Rojo    → red    (hard fail, blocks reception)
  //   NoConforme on Amarillo or Observado → amber (yellow light)
  //   Pendiente / Validando → neutral
  let borderColor: string
  if (alert.estado === 'Conforme') borderColor = '#2a8a87'
  else if (isPending) borderColor = colors.border
  else if (alert.estado === 'NoConforme' && alert.severidad === 'Rojo') borderColor = '#c8442f'
  else borderColor = '#EAB308' // Observado, or NoConforme on Amarillo

  const isProblem = alert.estado === 'NoConforme' || alert.estado === 'Observado'

  return (
    <Box
      bg={colors.bgCard}
      borderWidth="1px"
      borderColor={isProblem ? `${borderColor}55` : colors.border}
      borderLeftWidth="4px"
      borderLeftColor={borderColor}
      borderRadius="md"
      p={3}
    >
      <HStack justify="space-between" align="flex-start" mb={2}>
        <VStack gap={0.5} alignItems="flex-start" flex={1}>
          <Text fontSize="sm" fontWeight="700" color={colors.text}>
            {alert.nombre}
          </Text>
          <HStack gap={2}>
            <Text fontSize="xs" color={colors.textMuted}>
              {alert.grupoNombre}
            </Text>
            <Text fontSize="xs" color={colors.textMuted}>
              ·
            </Text>
            <Text fontSize="xs" fontFamily="'JetBrains Mono', monospace" color={colors.textMuted}>
              {alert.codigo}
            </Text>
          </HStack>
        </VStack>
        <Box
          px={2}
          py={0.5}
          borderRadius="full"
          fontSize="10px"
          fontWeight="700"
          bg={`${estadoColor}22`}
          color={estadoColor}
          whiteSpace="nowrap"
        >
          {alert.estado.toUpperCase()}
        </Box>
      </HStack>

      {alert.observation && (
        <Text fontSize="xs" color={colors.textMuted} mb={2}>
          {alert.observation}
        </Text>
      )}

      {alert.displayCard?.rows && alert.displayCard.rows.length > 0 && (
        <VStack gap={0.5} alignItems="stretch" mt={2}>
          {alert.displayCard.rows.map((r, idx) => (
            <HStack key={`${alert.codigo}-${idx}`} justify="space-between" gap={3}>
              <Text fontSize="xs" color={colors.textMuted}>
                {r.label}
              </Text>
              <Text
                fontSize="xs"
                color={r.variant === 'negative' ? '#c8442f' : colors.text}
                fontWeight="600"
                textAlign="right"
              >
                {r.value}
              </Text>
            </HStack>
          ))}
        </VStack>
      )}

      {alert.requiereInputManual && (
        <Box mt={2} fontSize="11px" color={colors.textMuted} fontStyle="italic">
          Requiere verificación manual al recibir
        </Box>
      )}
    </Box>
  )
}

// ────────────────────────────────────────────────────────────────────

export default function ControlIngresoDetallePage() {
  const colors = themeColors
  const { t } = useT()
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [data, setData] = useState<PlantGateDetailResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [refreshing, setRefreshing] = useState(false)
  const [error, setError] = useState<string | null>(null)
  /** Popup abierto en el mapa: 'last' (última señal) o 'gate' (garita/planta). */
  const [openPopup, setOpenPopup] = useState<'last' | 'gate' | null>(null)

  useEffect(() => {
    if (!id) return
    setLoading(true)
    plantGateService
      .getDetalle(id)
      .then((r) => setData(r))
      .catch(() => setError('No se pudo cargar el detalle'))
      .finally(() => setLoading(false))
  }, [id])

  const handleRefresh = async () => {
    if (!id || refreshing) return
    setRefreshing(true)
    try {
      const fresh = await plantGateService.refresh(id)
      setData(fresh)
    } catch {
      setError('No se pudo refrescar')
    } finally {
      setRefreshing(false)
    }
  }

  if (loading) {
    return (
      <Flex justify="center" align="center" minH="40vh">
        <Spinner color={brand.primary} />
      </Flex>
    )
  }

  if (error || !data) {
    return (
      <VStack gap={4} alignItems="stretch">
        <Text color="#c8442f">{error || 'No encontrado'}</Text>
        <Box
          as="button"
          onClick={() => navigate('/app/planta/garita')}
          color={brand.primary}
          fontWeight="600"
        >
          Volver
        </Box>
      </VStack>
    )
  }

  const trailLatLngs: LL[] = data.trail.map((p) => ({ lat: p.latitude, lng: p.longitude }))
  const lastPoint = data.trail.length === 0 ? null : data.trail[data.trail.length - 1]
  const allMapPoints: LL[] = [...trailLatLngs]
  if (data.plantCoord) {
    allMapPoints.push({ lat: data.plantCoord.latitude, lng: data.plantCoord.longitude })
  }

  const etaCopy = ETA_COPY[data.etaState]

  const groupedAlerts = data.alerts.reduce<Record<string, PlantGateAlertResponse[]>>((acc, a) => {
    const key = a.grupoNombre || 'Otros'
    acc[key] = acc[key] ?? []
    acc[key].push(a)
    return acc
  }, {})

  return (
    <VStack gap={5} alignItems="stretch">
      {/* Header */}
      <HStack justify="space-between" wrap="wrap" gap={3}>
        <HStack gap={3}>
          <Box
            as="button"
            onClick={() => navigate('/app/planta/garita')}
            display="inline-flex"
            alignItems="center"
            gap={2}
            color={colors.textMuted}
            fontSize="sm"
            _hover={{ color: brand.primary }}
          >
            <LuArrowLeft size={16} />
            {t('plantGate.detail.back', { defaultValue: 'Volver' })}
          </Box>
          <VStack gap={0} alignItems="flex-start">
            <Text fontSize="xs" color={colors.textMuted} textTransform="uppercase" letterSpacing="0.06em">
              {t('plantGate.detail.title', { defaultValue: 'Control de Ingreso' })}
            </Text>
            <HStack gap={2}>
              <Text fontFamily="'JetBrains Mono', monospace" fontSize="lg" fontWeight="700" color={colors.text}>
                {data.batchCode}
              </Text>
              <Box
                px={2}
                py={0.5}
                borderRadius="full"
                fontSize="10px"
                fontWeight="700"
                bg="#2f2a26"
                color="white"
              >
                {data.shipmentStateName}
              </Box>
            </HStack>
          </VStack>
        </HStack>

        <HStack gap={2}>
          <Box
            display="inline-flex"
            alignItems="center"
            gap={1.5}
            px={3}
            py={1.5}
            borderRadius="full"
            fontSize="12px"
            fontWeight="700"
            bg={`${etaCopy.color}22`}
            color={etaCopy.color}
          >
            <LuMapPin size={12} />
            {etaCopy.label(data.etaMinutes)}
            {data.distanceMeters != null && data.etaState !== 'Unknown' && (
              <Text fontSize="11px" opacity={0.8} ml={1}>
                · {formatDistance(data.distanceMeters)}
              </Text>
            )}
          </Box>
          <Box
            as="button"
            onClick={handleRefresh}
            display="inline-flex"
            alignItems="center"
            gap={2}
            px={3}
            py={1.5}
            borderRadius="md"
            border="1px solid"
            borderColor={colors.border}
            color={colors.text}
            fontSize="sm"
            fontWeight="600"
            cursor={refreshing ? 'not-allowed' : 'pointer'}
            opacity={refreshing ? 0.6 : 1}
            pointerEvents={refreshing ? 'none' : 'auto'}
            _hover={{ bg: colors.bgCard }}
          >
            <LuRefreshCw size={14} />
            {refreshing ? '…' : 'Refrescar'}
          </Box>
        </HStack>
      </HStack>

      {/* Counts strip */}
      <HStack gap={3} wrap="wrap">
        <Box
          flex={1}
          minW="140px"
          p={3}
          borderRadius="md"
          bg="#c8442f11"
          borderWidth="1px"
          borderColor="#c8442f33"
        >
          <HStack gap={2} mb={1}>
            <LuSiren color="#c8442f" size={16} />
            <Text fontSize="11px" color="#c8442f" fontWeight="700" textTransform="uppercase">
              Alertas graves
            </Text>
          </HStack>
          <Text fontSize="2xl" fontWeight="800" color="#c8442f">
            {data.alertasGravesCount}
          </Text>
        </Box>
        <Box
          flex={1}
          minW="140px"
          p={3}
          borderRadius="md"
          bg="#EAB30811"
          borderWidth="1px"
          borderColor="#EAB30833"
        >
          <HStack gap={2} mb={1}>
            <LuTriangleAlert color="#8a5f0b" size={16} />
            <Text fontSize="11px" color="#8a5f0b" fontWeight="700" textTransform="uppercase">
              Observaciones
            </Text>
          </HStack>
          <Text fontSize="2xl" fontWeight="800" color="#8a5f0b">
            {data.observacionesCount}
          </Text>
        </Box>
        <Box
          flex={1}
          minW="140px"
          p={3}
          borderRadius="md"
          bg={colors.bgCard}
          borderWidth="1px"
          borderColor={colors.border}
        >
          <HStack gap={2} mb={1}>
            <LuClock color={colors.textMuted} size={16} />
            <Text fontSize="11px" color={colors.textMuted} fontWeight="700" textTransform="uppercase">
              Pendientes
            </Text>
          </HStack>
          <Text fontSize="2xl" fontWeight="800" color={colors.text}>
            {data.pendientesCount}
          </Text>
        </Box>
      </HStack>

      {/* Pre-validation disclaimer */}
      <Box
        p={3}
        borderRadius="md"
        bg="#3d6fa811"
        borderWidth="1px"
        borderColor="#3d6fa833"
        color={colors.text}
        fontSize="xs"
        lineHeight="1.5"
      >
        <strong>Pre-validación:</strong> esta vista solo consulta datos almacenados en el
        sistema, no contra servicios externos (SUNAT, IGAFOM, etc.). La validación oficial se
        realiza al momento de recibir el camión con la app móvil, y puede mostrar alertas
        adicionales que dependan de información en línea.
      </Box>

      {/* Plant coord banner */}
      {!data.plantCoord && (
        <Box
          p={3}
          borderRadius="md"
          bg="#EAB30811"
          borderWidth="1px"
          borderColor="#EAB30833"
          color="#92400E"
          fontSize="sm"
        >
          Configura la <strong>garita</strong> de tu planta o un <strong>polígono</strong> para activar la
          estimación de tiempo de llegada.
        </Box>
      )}

      <Grid templateColumns={{ base: '1fr', lg: '1.4fr 1fr' }} gap={4}>
        {/* Map */}
        <Box
          bg={colors.bgCard}
          borderWidth="1px"
          borderColor={colors.border}
          borderRadius="lg"
          overflow="hidden"
          h={{ base: '320px', lg: '520px' }}
          position="relative"
        >
          {trailLatLngs.length === 0 && !data.plantCoord ? (
            <Flex h="100%" justify="center" align="center" color={colors.textMuted}>
              <Text fontSize="sm">Sin puntos GPS para mostrar</Text>
            </Flex>
          ) : (
            <KMap
              defaultCenter={
                allMapPoints[0] ??
                (data.plantCoord
                  ? { lat: data.plantCoord.latitude, lng: data.plantCoord.longitude }
                  : { lat: -12, lng: -77 })
              }
              defaultZoom={13}
              mapTypeId="hybrid"
            >
              <FitBounds points={allMapPoints} padding={40} maxZoom={15} />
              {trailLatLngs.length > 1 && (
                <Polyline path={trailLatLngs} strokeColor={brand.primary} strokeWeight={4} strokeOpacity={0.7} />
              )}
              {lastPoint && (
                <AdvancedMarker
                  position={{ lat: lastPoint.latitude, lng: lastPoint.longitude }}
                  onClick={() => setOpenPopup((cur) => (cur === 'last' ? null : 'last'))}
                >
                  <ElementPin kind="lastGps" size={32} />
                </AdvancedMarker>
              )}
              {lastPoint && openPopup === 'last' && (
                <InfoWindow
                  position={{ lat: lastPoint.latitude, lng: lastPoint.longitude }}
                  pixelOffset={[0, -34]}
                  onCloseClick={() => setOpenPopup(null)}
                >
                  <Box fontFamily="'Instrument Sans', sans-serif">
                    <strong>Última señal</strong>
                    <br />
                    {formatDateTime(lastPoint.capturedAt)}
                  </Box>
                </InfoWindow>
              )}
              {data.plantCoord && (
                <AdvancedMarker
                  position={{ lat: data.plantCoord.latitude, lng: data.plantCoord.longitude }}
                  onClick={() => setOpenPopup((cur) => (cur === 'gate' ? null : 'gate'))}
                >
                  <ElementPin kind={data.plantCoord.source === 'garita' ? 'gate' : 'plantCenter'} size={38} />
                </AdvancedMarker>
              )}
              {data.plantCoord && openPopup === 'gate' && (
                <InfoWindow
                  position={{ lat: data.plantCoord.latitude, lng: data.plantCoord.longitude }}
                  pixelOffset={[0, -40]}
                  onCloseClick={() => setOpenPopup(null)}
                >
                  <Box fontFamily="'Instrument Sans', sans-serif">
                    <strong>{data.plantCoord.source === 'garita' ? 'Garita' : 'Centro de planta'}</strong>
                  </Box>
                </InfoWindow>
              )}
            </KMap>
          )}
        </Box>

        {/* Side info */}
        <VStack gap={4} alignItems="stretch">
          {/* Shipment metadata */}
          <Box bg={colors.bgCard} borderWidth="1px" borderColor={colors.border} borderRadius="lg" p={4}>
            <Text fontSize="sm" fontWeight="700" color={colors.text} mb={3}>
              Datos del traslado
            </Text>
            <Grid templateColumns="1fr 1fr" gap={3}>
              <Field label="Proveedor" value={data.supplierName} />
              <Field label="RUC" value={data.supplierRuc} />
              <Field label="Derecho minero" value={data.miningRightName} />
              <Field label="Mineral" value={data.mineralTypeName} />
              <Field
                label="Carga"
                value={
                  data.mineralWeight
                    ? `${data.mineralWeight} ${data.measurementUnitName ?? ''}`.trim()
                    : '—'
                }
              />
              <Field label="Acopio" value={data.collectionCenterName} />
              <Field label="Vehículo" value={data.vehiclePlate} />
              <Field label="Tipo" value={data.vehicleTypeName} />
              <Field label="Conductor" value={data.driverName} />
              <Field label="Licencia" value={data.driverLicense} />
              <Field label="Guía despacho" value={data.dispatchGuideNumber} />
              <Field label="Guía transporte" value={data.transportGuideNumber} />
              <Field label="Última señal" value={formatDateTime(data.lastPointAt)} />
              <Field
                label={data.etaState === 'Finished' ? 'Distancia a planta al finalizar' : 'Distancia a planta'}
                value={formatDistance(data.distanceMeters)}
              />
              <Field
                label="Velocidad actual"
                value={data.averageSpeedKmh != null ? `${data.averageSpeedKmh.toFixed(1)} km/h` : '—'}
              />
              <Field
                label="Velocidad referencia"
                value={data.cruisingSpeedKmh != null ? `${data.cruisingSpeedKmh.toFixed(1)} km/h` : '—'}
              />
            </Grid>
          </Box>
        </VStack>
      </Grid>

      {/* Alerts */}
      <VStack gap={3} alignItems="stretch">
        <Text fontSize="sm" fontWeight="700" color={colors.text}>
          Validaciones que se evaluarán al recibir
        </Text>
        {Object.keys(groupedAlerts).length === 0 ? (
          <Box
            p={4}
            borderRadius="md"
            bg={colors.bgCard}
            borderWidth="1px"
            borderColor={colors.border}
          >
            <Text fontSize="sm" color={colors.textMuted}>
              No hay validaciones aplicables.
            </Text>
          </Box>
        ) : (
          Object.entries(groupedAlerts).map(([grupo, alerts]) => (
            <Box key={grupo}>
              <HStack gap={2} mb={2}>
                <LuCircle size={8} color={colors.textMuted} />
                <Text fontSize="xs" color={colors.textMuted} fontWeight="700" textTransform="uppercase" letterSpacing="0.06em">
                  {grupo}
                </Text>
              </HStack>
              <Grid templateColumns={{ base: '1fr', md: '1fr 1fr' }} gap={3}>
                {alerts.map((a) => (
                  <AlertCard key={a.validationConfigId} alert={a} />
                ))}
              </Grid>
            </Box>
          ))
        )}
      </VStack>
    </VStack>
  )
}
